import {
  useProjectLabels,
  useProjectLabelFilter,
} from '@features/projects/labels/project-labels.hooks';
import { useCallback, useEffect, useRef } from 'react';
import { OneOfProjectListElements } from '@shared/models/project.model';
import {
  createSortFilterStore,
  getSortFilteredChildren,
} from './use-filter.hook';
import { isProjectInfo } from '@shared/helpers/project-folder.helpers';
import { createLocalStorageStore } from '../helpers/local-storage.helpers';
import {
  SortByAlternativeKey,
  SortingOption,
  SortDirection,
} from '@shared/helpers/tree.helpers';
import { useUserId, useUserLookup } from './use-user.hooks';
import { useUIState } from '@client/store/ui';
import { searchFilter } from '@shared/helpers/string.helpers';
import { getElementName } from '@shared/helpers/element.helpers';
import {
  useFilterProjectListResults,
  useGetResultsPerGFA,
} from '../../features/results/use-results.hooks';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { useSearchParam } from './use-search-param.hook';
import { useProjectsForOverview } from '@client/store/project';
import { compare } from '@shared/helpers/compare.helpers';
import { useSharedMemo } from './use-utils.hooks';
import {
  nestSortedProjectList,
  matchesProjectLabelFilter,
  matchesProjectProgramFilter,
} from './use-filter-projects.helpers';

type SortProjectsOptionKey = 'cost' | 'co2';

export type SortProjectsOptions = OneOfProjectListElements &
  Record<SortProjectsOptionKey, number>;

export const { useStore: useSortProjectsBy, set: setSortProjectsBy } =
  createLocalStorageStore<SortByAlternativeKey<SortProjectsOptions>>(
    'sort_projects_by',
    'name',
  );

export const {
  useStore: useSortProjectsDirection,
  set: setSortProjectsDirection,
} = createLocalStorageStore<SortDirection>('sort_projects_direction', 'asc');

export const useProjectSearchString = () => useSearchParam('p-search', '');
export const useProjectOwnerFilter = () => useSearchParam('p-owner', '');
export const useProjectProgramFilter = () => useSearchParam('p-programs', '');

const PROJECT_LIST_KIND_SORT: SortingOption<OneOfProjectListElements>[] = [
  {
    id: 'projects-folder-sort',
    property: 'kind',
    direction: 'asc',
  },
];

const PROJECT_LIST_CHILD_OPTIONS = {
  childrenKey: 'elements' as const,
  sorting: PROJECT_LIST_KIND_SORT,
  compareFn: compare.equal.byId,
};

const { setSortingOption, setFilter, useSortFilteredItems } =
  createSortFilterStore<OneOfProjectListElements>({
    childrenKey: 'elements',
    name: 'ProjectListSorting',
    sorting: PROJECT_LIST_KIND_SORT,
    compareFn: compare.equal.byId,
  });

/**
 * Sorted, filtered project/folder tree. Folders already carry nested children.
 * Templates are excluded from the overview.
 */
export const useSortedProjects = (): OneOfProjectListElements[] => {
  const tree = useProjectsForOverview();
  const filteredItems = useSortFilteredItems(tree);

  return useSharedMemo(
    () => {
      const getChildren = (item?: OneOfProjectListElements) =>
        getSortFilteredChildren(
          filteredItems,
          item,
          PROJECT_LIST_CHILD_OPTIONS,
        );
      return nestSortedProjectList(getChildren(), getChildren);
    },
    'useSortedProjects.nestedRoots',
    [filteredItems],
  );
};

/**
 * Initialize sorting and filtering of projects
 * @param userId - User ID to filter projects by
 */
export const useInitProjectsSortFilter = (userId?: string) => {
  useInitProjectsSorting();
  useInitProjectsFilter(userId);
};

/**
 * Initialize filtering of projects
 * @param userId - User ID to filter projects by (optional)
 */
const useInitProjectsFilter = (userId?: string) => {
  const { assignments } = useProjectLabels();
  const [labelFilter] = useProjectLabelFilter();
  const [ownerFilter] = useProjectOwnerFilter();
  const [programFilter] = useProjectProgramFilter();
  const currentUserId = useUserId();
  const ownerId = userId ?? (ownerFilter === 'me' ? currentUserId : undefined);

  useEffect(() => {
    const programs = programFilter.split(',').filter(Boolean);
    setFilter({
      id: 'projects-program-filter',
      disabled: programs.length === 0,
      function: (item) => matchesProjectProgramFilter(item, programs),
    });
  }, [programFilter]);
  const users = useUserLookup();
  const [searchString] = useProjectSearchString();

  useEffect(() => {
    const selectedIds = labelFilter.split(',').filter(Boolean);
    setFilter({
      id: 'projects-label-filter',
      disabled: selectedIds.length === 0,
      function: (item) =>
        matchesProjectLabelFilter(item, selectedIds, assignments),
    });
  }, [labelFilter, assignments]);

  // Filter projects by owner name or project name
  useEffect(() => {
    setFilter({
      id: 'projects-serach-filter',
      function: (item, path) => {
        const name = getElementName(item);
        const owner = isProjectInfo(item) ? users[item.owner]?.name : '';

        const pathNames = path.flatMap((pathItem) => {
          if (isProjectInfo(pathItem)) {
            const { name, owner } = pathItem;
            const userName = users[owner]?.name ?? '';

            return [name, userName];
          }
          return pathItem.name;
        });

        return searchFilter(
          [name, owner, ...pathNames].join(' '),
          searchString,
        );
      },
    });
  }, [searchString, users]);

  // Filter projects by owner if userId is provided
  useEffect(() => {
    setFilter({
      id: 'projects-owner-filter',
      function: (item) => {
        return 'owner' in item === false || item.owner === ownerId;
      },
      disabled: !ownerId,
    });
  }, [ownerId]);
};

/**
 * Initialize sorting of projects
 */
const useInitProjectsSorting = () => {
  const sortFn = useSortFn();
  const sortBy = useSortProjectsBy();
  const direction = useSortProjectsDirection();

  useEffect(() => {
    setSortingOption({
      id: 'projects-sort',
      direction,
      property: sortFn,
    });
  }, [sortBy, sortFn, direction]);
};

const useSortFn = () => {
  const getResultsPerGFA = useGetResultsPerGFA();
  const lifecycleFilter = useFilterProjectListResults();
  const users = useUserLookup();
  const sortBy = useSortProjectsBy();
  const { addedElementId } = useUIState('addedElementId');

  const sortRef = useRef<SortByAlternativeKey<SortProjectsOptions>>(sortBy);
  const itemRef = useRef('');

  return useCallback(
    (item: OneOfProjectListElements) => {
      // place new element at the top of the list
      if (sortRef.current === sortBy && itemRef.current === item.id) {
        return sortBy === 'gfa' ? undefined : true;
      }
      if (addedElementId) {
        itemRef.current = addedElementId;
      }
      if (sortRef.current !== sortBy) {
        sortRef.current = sortBy;
        itemRef.current = '';
      }

      switch (sortBy) {
        case 'name': {
          return item.name.toLowerCase();
        }
        case 'gfa': {
          return item.gfa ?? 0;
        }
        case 'target': {
          return item.target ?? 0;
        }
        case 'updated_at': {
          return item.updated_at;
        }
        case 'completed_at': {
          return item.completed_at ?? 0;
        }
        case 'owner': {
          if (isProjectInfo(item)) {
            const { owner: id } = item;
            return users[id]?.name;
          }
          return '';
        }
        case 'cost':
        case 'co2': {
          return getConversionFactorValue(
            getResultsPerGFA(lifecycleFilter(item.results)),
            sortBy === 'co2' ? 'co2e' : 'sek',
          );
        }
        default:
          return 0;
      }
    },
    [addedElementId, getResultsPerGFA, lifecycleFilter, sortBy, users],
  );
};
